/**
 * A link or picture the Genie put on screen mid-conversation via the
 * `showRelatedMedia` client tool. Shape mirrors MediaItem in
 * supabase/functions/handle-conversation/resolve-media.ts.
 */
export interface GenieAttachment {
  kind: "link" | "image";
  url: string;
  title: string;
  description?: string | null;
  /** Preview image for a link card (e.g. the page's og:image). */
  imageUrl?: string | null;
  /** Page a web image was found on; the full-screen view links to it as the source. */
  pageUrl?: string | null;
  source: "resource" | "knowledge" | "product" | "web";
  /** How well it fits what the visitor asked, 1-10, scored by resolve-media. */
  relevance?: number;
}

const SOURCE_ORDER: Record<GenieAttachment["source"], number> = { resource: 0, knowledge: 1, product: 2, web: 3 };

/** Sort order, best first: the higher relevance, then the owner's own content ahead of what was found elsewhere. */
export const compareAttachments = (a: GenieAttachment, b: GenieAttachment): number =>
  (b.relevance ?? 0) - (a.relevance ?? 0) || SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source];

/** The messages with the latest card for `replacesUrl` swapped for `attachment`, in the same place. */
export const replaceAttachment = <T extends { attachment?: GenieAttachment }>(
  messages: T[],
  replacesUrl: string,
  attachment: GenieAttachment,
): T[] => {
  const index = messages.map((message) => message.attachment?.url).lastIndexOf(replacesUrl);
  if (index === -1) return messages;
  const next = [...messages];
  next[index] = { ...messages[index], attachment };
  return next;
};

export interface ResolveMediaResult {
  status: "found" | "not_found";
  items: GenieAttachment[];
}

/**
 * Header of the contextual update sent when a slow lookup lands after the tool
 * already answered. It tells the agent what is now on screen, and the stored
 * transcript keeps it, so the dashboard can render those items too.
 */
const LATE_MEDIA_HEADER = "[ON-SCREEN MEDIA SHOWN - DO NOT READ ALOUD]";

export const formatLateMediaUpdate = (items: GenieAttachment[], replaced: GenieAttachment[] = []): string =>
  [
    LATE_MEDIA_HEADER,
    `The visitor can now see in the chat: ${items.map((item) => item.title).join("; ")}.` +
      (replaced.length > 0
        ? ` It took the place of ${replaced.map((item) => item.title).join("; ")}, which is no longer on screen: do not mention that.`
        : "") +
      " You may mention it briefly.",
    JSON.stringify({ status: "shown", items }),
  ].join("\n");

/** Items from a showRelatedMedia result or a late-media contextual update, or null for anything else. */
export const parseSharedMedia = (value?: string | null): GenieAttachment[] | null => {
  if (!value) return null;
  const json = value.startsWith(LATE_MEDIA_HEADER) ? value.slice(value.lastIndexOf("\n") + 1) : value;
  try {
    const parsed = JSON.parse(json);
    return parsed?.status === "shown" && Array.isArray(parsed.items) && parsed.items.length > 0 ? parsed.items : null;
  } catch {
    return null;
  }
};
